import React, {useMemo} from 'react';
import {Provider as EppoProvider} from '@ezcater/eppo-react';
import {Flag, offlineInit} from '@eppo/js-client-sdk';
import {parseUrlParams} from '@src/util/urls';
import extractOverrides from '@src/util/extractOverrides';
import {useIdentity} from '../../hooks/useIdentity';

interface AssignmentsProviderProps {
  children: React.ReactNode;
  flagsConfiguration: Record<string, Flag>;
  overridesConfiguration?: Record<string, boolean | string | number | object>;
  requestUrl?: string;
  trackingId?: string;
}

const AssignmentsProvider: React.FC<AssignmentsProviderProps> = ({
  children,
  flagsConfiguration,
  overridesConfiguration,
  requestUrl,
  trackingId = '',
}) => {
  const {me} = useIdentity();
  const authTrackingId = me?.trackingId;

  // Extract overrides from the URL query parameters
  const urlOverrides = useMemo(() => {
    const url = requestUrl || '';
    const parsedParams = url ? parseUrlParams(url) : {};
    return extractOverrides(parsedParams, '__featureFlags', value => {
      if (value === 'true') return true;
      if (value === 'false') return false;
      return value;
    });
  }, [requestUrl]);
  const overrides = useMemo(() => {
    return {
      ...(overridesConfiguration || {}),
      ...urlOverrides,
    };
  }, [overridesConfiguration, urlOverrides]);

  /*
   * We are intentionally using useMemo (and not useEffect) here:
   * - offlineInit MUST be called prior to any getInstance() call
   * - useEffect will prevent both Server Side and Client Side Render from being
   * able to read the Eppo store
   *
   * Under the hood, the Eppo SDK creates a singleton that is referenced via
   * getInstance(), so we don't need the return value.
   */
  useMemo(() => {
    offlineInit({
      flagsConfiguration,
    });
  }, [flagsConfiguration]);

  const subjectAttributes = useMemo(() => {
    const catererBrandIds =
      me?.currentAccount?.brands?.map((brand: {id: string | number}) => `brand:${brand.id}`) || [];

    return {
      catererBrandIds,
    };
  }, [me?.currentAccount?.brands]);

  return (
    <EppoProvider
      subjectKey={authTrackingId || trackingId}
      subjectAttributes={subjectAttributes}
      overrides={overrides}
    >
      {children}
    </EppoProvider>
  );
};

export default AssignmentsProvider;
